import { SupervisorAccountOutlined } from "@mui/icons-material";
import { useCallback, useEffect, useState } from "react";
import {
  AccountAPI,
  AccountTypeAPI,
  OrganizationAPI,
  ProcessAPI,
  TaskExecutionAPI,
  ContactAPI,
} from "../../../api";
import useFeature from "../../../hooks/useFeature";
import { useGlobalOrganizationContext } from "../../../hooks/useGlobalOrganizationContext";
import { useAlert } from "../../../lib/alert";
import { Account, AccountDto } from "../../../model/Account";
import {
  AccountSearchOption,
  ESAccount,
} from "../../../model/AccountSearchOption";
import { AccountType } from "../../../model/AccountType";
import { ContactLabels, ContactType } from "../../../model/Contact";
import Organization from "../../../model/Organization";
import { OrganizationMember } from "../../../model/User";
import {
  TaskCreateAccount,
  TaskCreateAccountExecution,
  TaskExecutionComponentProps,
  TaskExecutionState,
} from "../../task";
import useOrganizationDirectory from "../../task/hooks/useOrganizationDirectory";
import { getAccountName } from "../utils";

export enum AccountDisplay {
  EMAIL_PROMPT = 0,
  ACCOUNT_SELECTOR = 1,
}

const useAccountExecution = ({
  task,
  taskExecution,
  processExecution,
  ...props
}: TaskExecutionComponentProps<
  TaskCreateAccount,
  TaskCreateAccountExecution
>) => {
  const showExternalAccounts = useFeature("showExternalAccounts", false);
  const { organization } = useGlobalOrganizationContext();
  const [accountType, setAccountType] = useState<AccountType | undefined>();
  const [actionBarVisible] = useState<boolean>(true);
  const [results, setResults] = useState<AccountSearchOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const { allAccounts, addAccounts, findAccount } = useOrganizationDirectory(
    organization!,
    accountType,
  );
  const alert = useAlert();

  const [selected, setSelected] = useState<AccountSearchOption | undefined>(
    () => {
      const account = taskExecution?.data?.account;
      const name = getAccountName(account);

      if (!account || name === "undefined") return undefined;

      const esAccount: ESAccount = {
        id: account.id || "",
        account_name: account.account_name,
        primary_email: account.primary_email,
        org_id: account.org_id,
        account_type_id: account.account_type_id,
        external_org_id: account.external_org_id,
      };

      return {
        name: name,
        data: esAccount,
        type: "account",
      };
    },
  );

  const [disabled, setDisabled] = useState<boolean>(
    taskExecution?.completed_at != null,
  );

  const getSelectedOption = (
    selected: AccountSearchOption,
  ): Partial<Account> | undefined => {
    if (selected.type === "account") {
      return selected.data as Account;
    }

    if (showExternalAccounts && selected.type === "external") {
      const external = selected.data as Partial<Account>;
      if (!external.account_name || !accountType || !organization)
        return undefined;

      return {
        id: external.id,
        account_name: external.account_name,
        primary_email: external.primary_email,
        account_type_id: accountType.id,
        org_id: organization.id,
      };
    }

    return undefined;
  };

  const fetchAccounts = useCallback(
    async (query: string) => {
      const exclusions = [];
      if (organization?.id) {
        const excludedMembers = await OrganizationAPI.getMembers(
          organization.id,
        );
        const excludedMemberIds = excludedMembers.map((e) => e.id);
        excludedMemberIds.forEach((id) => {
          const newId = `${id}_${organization.id}`;
          exclusions.push(newId);
        });
        exclusions.push(organization.id);
      }

      if (!showExternalAccounts) {
        exclusions.push("external-accounts");
      }
      const hideMembers = !(
        await ProcessAPI.get(processExecution?.process_id || "")
      ).entity_id;
      if (hideMembers) exclusions.push("members");
      setLoading(true);
      const results = await AccountAPI.directorySearch(
        query,
        accountType?.id,
        exclusions,
        organization?.id,
      );

      setResults(results);
      setLoading(false);
    },
    [
      processExecution?.process_id,
      organization?.id,
      accountType?.id,
      showExternalAccounts,
    ],
  );

  const onExecute = async (selected: AccountSearchOption | undefined) => {
    if (!selected) return;

    if (showExternalAccounts && selected.type === "external") {
      const externalData = selected.data as Partial<AccountDto>;

      if (!accountType || !organization || !externalData.id) return;

      const newAccount: Account = {
        id: "", //overwritten automatically
        account_name: externalData.account_name ?? "Unnamed Agency",
        primary_email: externalData.primary_email ?? "",
        org_id: organization.id,
        account_type_id: accountType.id,
        account_type_key: accountType.key,
        account_type: accountType.name,
      };

      const existing = findAccount(newAccount);
      if (!existing) {
        await AccountAPI.create(newAccount);
        addAccounts([newAccount]);
      }

      selected = {
        id: newAccount.id,
        name: newAccount.account_name,
        data: newAccount,
        type: "account",
        icon: SupervisorAccountOutlined,
      };
    } else if (selected.type === "organization") {
      const org = selected.data as Organization;

      if (!accountType || !organization) return;

      const newAccount: Account = {
        account_name: org.name,
        id: org.id,
        org_id: organization.id,
        account_type: accountType.name,
        account_type_id: accountType.id,
        external_org_id: org.id,
        primary_email: org.email || undefined,
      };
      const created = await AccountAPI.create(newAccount);
      addAccounts([created]);

      selected = {
        id: created.id,
        name: created.account_name,
        data: created,
        type: "account",
        icon: SupervisorAccountOutlined,
      };
    } else if (selected.type === "member") {
      const member = selected.data as OrganizationMember;
      const org = await OrganizationAPI.get(member.organizationId);

      if (!accountType || !organization) return;

      // Create parent account if it does not exist already
      // 1 Check if account already exists
      const res = await AccountAPI.getByOrganizationAndExternalOrganizationId(
        organization.id,
        org.id,
      );

      let parentAccount: Account;
      if (!res[0]) {
        // Parent account does not exist
        const email = await ContactAPI.findOneByDetailsGlobal(
          org.id,
          ContactLabels.PRIMARY,
          ContactType.EMAIL,
        );
        const newAccount: Account = {
          account_name: org.name,
          id: org.id,
          org_id: organization.id,
          account_type: accountType.name,
          account_type_id: accountType.id,
          external_org_id: org.id,
          primary_email: email?.value || org.email || "",
        };
        parentAccount = await AccountAPI.create(newAccount);
        await AccountAPI.createShareKey(parentAccount.id);
        addAccounts([parentAccount]);
      } else {
        // Parent account exists
        parentAccount = res[0];
      }

      // If parent account is not able
      // to be found/generated, no good.
      if (!parentAccount) return;

      try {
        await TaskExecutionAPI.assignMemberToProcessRole(
          taskExecution?.id || "",
          member.user_id,
        );
      } catch (error) {
        alert.error("Failure selecting account.");
        console.error("Error assigning member to process role:", error);
        return;
      }

      // Add member to account as child
      try {
        await AccountAPI.addUser(parentAccount.id, member.user_id);
      } catch (error) {
        alert.error("Failure selecting account.");
        console.error("Error adding user to account:", error);
        return;
      }

      // 3 Finally, set active account to the parent account
      selected = {
        id: parentAccount.id,
        name: parentAccount.account_name,
        data: parentAccount,
        type: "account",
        icon: SupervisorAccountOutlined,
      };
    }

    const account = getSelectedOption(selected);
    if (!account) return;

    const data = {
      ...taskExecution,
      task_id: task.id,
      data: {
        account,
      },
      state: TaskExecutionState.Completed,
    };
    props.onExecute?.(data);
    setDisabled(true);
  };

  const executeAfterCreated = async (account: Account) => {
    const data = {
      ...taskExecution,
      task_id: task.id,
      data: {
        account: account,
      },
      state: TaskExecutionState.Completed,
    };
    await props.onExecute?.(data);
  };

  useEffect(() => {
    if (!organization) {
      return;
    }

    if (task.data?.accountTypeKey) {
      AccountTypeAPI.byKey(organization?.id, task.data.accountTypeKey).then(
        (accountType) => {
          setAccountType(accountType);
        },
      );
    }
  }, [task.data?.accountTypeKey, organization]);

  const automaticallyExecute = true;

  useEffect(() => {
    if (taskExecution?.completed_at) {
      setDisabled(true);
    }
  }, [taskExecution?.completed_at]);

  return {
    selected,
    setSelected,
    accountType,
    organization,
    allAccounts,
    addAccounts,
    findAccount,
    disabled,
    onExecute,
    actionBarVisible,
    automaticallyExecute,
    executeAfterCreated,
    task,
    taskExecution,
    fetchAccounts,
    results,
    loading,
  };
};

export default useAccountExecution;
